import { createURLParser, URLParserBase } from './createURLParser';

export interface UrlValidationOptions {
    allowedBaseUrl?: URLParserBase;
    allowRelativeUrls?: boolean;
}

/**
 * Validates a URL to mitigate SSRF attacks.
 * Blocks private/internal IPs and ensures URLs match allowed base domain.
 *
 * @param url - The URL to validate
 * @param options - Validation options
 * @returns true if URL is safe, false otherwise
 */
export function validateUrl(url: string, options: UrlValidationOptions = {}): boolean {
    const { allowedBaseUrl, allowRelativeUrls = true } = options;

    // Reject empty or whitespace-only URLs
    if (!url || !url.trim()) return false;
    if (isProtocolRelativeUrl(url)) return false;
    if (isRelativeUrl(url)) return allowRelativeUrls;

    try {
        const parser = createURLParser(url, allowedBaseUrl);
        const hostname = parser.hostname || '';
        const protocol = parser.protocol || '';

        if (!hostname || !protocol) return false;
        if (!isAllowedProtocol(protocol)) return false;
        if (!isAllowedHostname(hostname, allowedBaseUrl)) return false;
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Validates that the protocol is http or https
 */
function isAllowedProtocol(protocol: string): boolean {
    return ['http:', 'https:'].includes(protocol.toLowerCase());
}

/**
 * Validates that the hostname matches allowed base domain or current origin.
 * Allows exact match or subdomain.
 */
function isAllowedHostname(hostname: string, allowedBaseUrl?: URLParserBase): boolean {
    if (allowedBaseUrl?.href) {
        const allowedUrlParser = createURLParser(allowedBaseUrl.href);

        if (!allowedUrlParser.hostname) return false;
        // Restrict to allowed base domain
        return (
            hostname === allowedUrlParser.hostname ||
            hostname.endsWith(`.${allowedUrlParser.hostname}`)
        );
    }

    if (typeof window !== 'undefined' && window.location.hostname) {
        // Fallback: restrict to current origin if no allowedBaseUrl is provided
        const currentHost = window.location.hostname;
        return hostname === currentHost || hostname.endsWith(`.${currentHost}`);
    }

    // No base URL and no window context - reject for safety
    return false;
}

function isRelativeUrl(url: string): boolean {
    return url.startsWith('/') || !url.includes('://');
}

function isProtocolRelativeUrl(url: string): boolean {
    return url.startsWith('//');
}
